import {
	type Modifier,
	type SDK_Colors,
	modifierObject,
} from "@quickpoint.me/shared";
import {
	type ForwardedRef,
	type ReactElement,
	forwardRef,
	useContext,
	useEffect,
	useLayoutEffect,
	useState,
	memo,
} from "react";
import { useRecoilCallback, useRecoilValue } from "recoil";
import styled from "styled-components";
import {
	PageElementIsLoadedFamily,
	PageElementsFamily,
	PageMediaAspectsFamily,
	PageSizeAtom,
	createLocation,
} from "../../state/page";

import type { ElementProps } from "../../functionalComponents/RenderPage";
import NoPhoto from "../../icons/no_photo.svg?react";
import { FilterColors } from "../../state/filter";

import type { PageSizeAtomType } from "../../@types/viewerati";
import { SingleColumnInPortrait } from "../../state/ui";
import { IsInFlexBoxContext } from "../displayElements/Cell";

// keep this here for future reference
// ${props => props.$isInFlexBox ? "height: 100%; width: 100%;" : props.$isInLandscape ? "min-height: 0;" : ""}

const StyledImage = styled.img<{
	$baseFontSize: number;
	$filterColors: SDK_Colors;
	$imageWidth: number;
	$isInFlexBox: boolean;
	$isInLandscape: boolean;
	$modifier?: Modifier;
	$pageSize: PageSizeAtomType;
	$singleColumnInPortrait: boolean;
	$onePercentHeight: number;
	$onePercentWidth: number;
}>`

object-fit: contain;
width: ${(props) => (props.$singleColumnInPortrait ? "auto" : "100%")};
max-width: 100%;
/* height: ${(props) =>
	props.$isInFlexBox || !props.$isInLandscape
		? "auto"
		: "100%"}; // seems to fix safari */
height: inherit; // seems to fix safari
min-height: 0;
border-radius: inherit;
border-width:  ${(props) => {
	if (
		props.$imageWidth &&
		props.$modifier &&
		props.$modifier[modifierObject["border-thickness:"].keyword]
	) {
		return `${
			(props.$imageWidth / 400) *
			Number.parseInt(
				props.$modifier[modifierObject["border-thickness:"].keyword] || "0",
			)
		}px`;
	}
	return "unset";
}};



`;

interface ImageProps extends ElementProps {
	imageWidth: number;
	altText?: string;
}

const Image = forwardRef(
	(
		{ atomId, pageIndex, imageWidth, altText }: ImageProps,
		imageRef: ForwardedRef<HTMLImageElement>,
	): ReactElement<ImageProps> => {
		const key = createLocation(pageIndex, atomId);
		const elementData = useRecoilValue(PageElementsFamily(key)).data;
		const isInFlexBox = useContext(IsInFlexBoxContext);
		const pageSize = useRecoilValue(PageSizeAtom);
		const baseFontSize = pageSize.baseFontSize;
		const filterColors = useRecoilValue(FilterColors);
		const singleColumnInPortrait = useRecoilValue(SingleColumnInPortrait);

		const [imageError, setImageError] = useState(false);

		const url = elementData.url ? elementData.url : elementData.content;

		const setElementLoaded = useRecoilCallback(({ set }) => () => {
			set(PageElementIsLoadedFamily(createLocation(pageIndex, atomId)), true);
		}, [pageIndex, atomId, url]);

		const unSetElementLoaded = useRecoilCallback(({ set }) => () => {
			set(PageElementIsLoadedFamily(createLocation(pageIndex, atomId)), false);
		}, [pageIndex, atomId, url]);

		const recordAspect = useRecoilCallback(({ set }) => (img: HTMLImageElement) => {
			if (!img.naturalWidth || !img.naturalHeight) return;
			const aspect = img.naturalWidth / img.naturalHeight;
			set(PageMediaAspectsFamily(pageIndex), (prev) =>
				prev[atomId] === aspect ? prev : { ...prev, [atomId]: aspect });
		}, [pageIndex, atomId]);

		useEffect(() => {
			unSetElementLoaded();
		}, [url, unSetElementLoaded]);

		// If there's no URL, mark the element as loaded so AllLoaded can become true
		useEffect(() => {
			if (!url) {
				setElementLoaded();
			}
		}, [url, setElementLoaded]);

		// reset the image error if the URL changes so it can retry
		useLayoutEffect(() => {
			setImageError(false);
			elementData.url;
		}, [elementData.url]);

		if (!url) {
			return <NoPhoto style={{ width: "100%", height: "100%" }} />;
		}

		if (imageError && url) {
			return <NoPhoto style={{ width: "100%", height: "100%" }} />;
		}
		return (
			<StyledImage
				alt={altText}
				$baseFontSize={baseFontSize}
				$filterColors={filterColors}
				$imageWidth={imageWidth}
				$isInFlexBox={isInFlexBox}
				$isInLandscape={pageSize.landscape}
				$modifier={elementData.modifier}
				$onePercentHeight={pageSize.onePercentHeight}
				$onePercentWidth={pageSize.onePercentWidth}
				$pageSize={pageSize}
				$singleColumnInPortrait={singleColumnInPortrait}
				ref={imageRef}
				src={typeof url === "string" ? url : "unset"}
				onError={() => {
					setImageError(true);
					setElementLoaded();
				}}
				onLoad={(e) => {
					setElementLoaded();
					recordAspect(e.currentTarget);
				}}
			/>
		);
	},
);

export default memo(Image);
